import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { mapImageUrl } from "../api/queries";
import type { MapImageView } from "../api/types";
import { text } from "../text";
import { polygonPoints, type MapPolygon } from "./MapView";

const zoomLevels = [1, 2, 3, 4];

function round(value: number): number {
  return Math.round(Math.min(100, Math.max(0, value)) * 10) / 10;
}

/**
 * Draws polygons on a map image: a click on the map adds a point after the selected one, handles can be dragged,
 * moved with the arrow keys (Shift for larger steps) and removed with Delete. Coordinates have one decimal place.
 */
export function MapEditor(props: { system: string; image: MapImageView; value: MapPolygon[]; onChange: (value: MapPolygon[]) => void }) {
  const { value, onChange } = props;
  const frame = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1);
  const dragging = useRef(false);
  const polygon = value[active] ?? [];
  const marked = selected === null ? undefined : polygon[selected];

  const position = (clientX: number, clientY: number): number[] => {
    const rect = frame.current!.getBoundingClientRect();
    return [round(((clientX - rect.left) / rect.width) * 100), round(((clientY - rect.top) / rect.height) * 100)];
  };

  const update = (points: MapPolygon) => onChange(value.map((p, i) => (i === active ? points : p)));

  const addPoint = (e: PointerEvent<HTMLDivElement>) => {
    if ((e.target as Element).closest(".map-handle")) return;
    if (value.length === 0) {
      onChange([[position(e.clientX, e.clientY)]]);
      setActive(0);
      setSelected(0);
      return;
    }
    const index = selected === null ? polygon.length : selected + 1;
    update([...polygon.slice(0, index), position(e.clientX, e.clientY), ...polygon.slice(index)]);
    setSelected(index);
  };

  const removePoint = (index: number) => {
    update(polygon.filter((_, i) => i !== index));
    setSelected(polygon.length > 1 ? Math.max(0, index - 1) : null);
  };

  const onHandleKey = (e: KeyboardEvent, index: number) => {
    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      removePoint(index);
      return;
    }
    const step = e.shiftKey ? 1 : 0.1;
    const moves: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const delta = moves[e.key];
    if (!delta) return;
    e.preventDefault();
    const [x = 0, y = 0] = polygon[index] ?? [];
    update(polygon.map((p, i) => (i === index ? [round(x + delta[0]), round(y + delta[1])] : p)));
  };

  const addPolygon = () => {
    onChange([...value, []]);
    setActive(value.length);
    setSelected(null);
  };

  const removePolygon = () => {
    onChange(value.filter((_, i) => i !== active));
    setActive(Math.max(0, active - 1));
    setSelected(null);
  };

  return (
    <div className="map-editor stack">
      <div className="row" role="toolbar" aria-label="Umrisse">
        {value.map((p, index) => (
          <button
            key={index}
            type="button"
            className={`chip${index === active ? " selected" : ""}`}
            aria-pressed={index === active}
            onClick={() => {
              setActive(index);
              setSelected(null);
            }}
          >
            Polygon {index + 1} <span className="muted">({p.length})</span>
          </button>
        ))}
        <button type="button" className="button secondary" onClick={addPolygon}>
          Polygon hinzufügen
        </button>
        <button type="button" className="button secondary" disabled={value.length === 0} onClick={removePolygon}>
          Polygon löschen
        </button>
        <button type="button" className="button secondary" disabled={selected === null} onClick={() => selected !== null && removePoint(selected)}>
          Punkt löschen
        </button>
        <label className="field inline map-zoom">
          <span>Zoom</span>
          <select value={zoom} onChange={(e) => setZoom(Number(e.target.value))}>
            {zoomLevels.map((z) => (
              <option key={z} value={z}>
                {z * 100} %
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="muted small">
        Tippe auf die Karte, um hinter dem markierten Punkt einen Punkt einzufügen. Punkte lassen sich ziehen, mit den Pfeiltasten verschieben (Umschalt: größere Schritte) und
        mit Entf löschen. Für kleine Gebiete hilft der Zoom.
        {selected !== null && marked && (
          <>
            {" "}
            Markiert: Punkt {selected + 1} bei {marked[0]?.toFixed(1)} / {marked[1]?.toFixed(1)}.
          </>
        )}
      </p>
      <div className="map-scroll">
        <div
          ref={frame}
          className="map-frame map-editor-frame"
          style={{ aspectRatio: `${props.image.width} / ${props.image.height}`, width: `${zoom * 100}%` }}
          onPointerDown={addPoint}
          data-testid="map-editor-frame"
        >
          <img src={mapImageUrl(props.system, props.image)} alt={text(props.image.name)} draggable={false} />
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {value.map((p, index) => (
              <polygon key={index} className={`map-area${index === active ? " editing" : " inactive"}`} points={polygonPoints(p)} />
            ))}
          </svg>
          {polygon.map(([x = 0, y = 0], index) => (
            <button
              key={index}
              type="button"
              className={`map-handle${index === selected ? " selected" : ""}`}
              style={{ left: `${x}%`, top: `${y}%` }}
              aria-label={`Punkt ${index + 1}: ${x.toFixed(1)} / ${y.toFixed(1)}`}
              onFocus={() => setSelected(index)}
              onKeyDown={(e) => onHandleKey(e, index)}
              onPointerDown={(e) => {
                e.stopPropagation();
                e.currentTarget.setPointerCapture(e.pointerId);
                dragging.current = true;
                setSelected(index);
              }}
              onPointerMove={(e) => {
                if (!dragging.current || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
                const point = position(e.clientX, e.clientY);
                if (point[0] !== x || point[1] !== y) update(polygon.map((p, i) => (i === index ? point : p)));
              }}
              onPointerUp={(e) => {
                dragging.current = false;
                e.currentTarget.releasePointerCapture(e.pointerId);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
